/*
 * SidebarUser
 * @module Sidebar
 * @flow
 */

import React, { Fragment } from 'react';
import styled, { css } from 'styled-components/macro';
import { palette, font } from 'styled-theme';
import { ifProp, ifNotProp } from 'styled-tools';

import { Avatar } from '~/components/Avatar';
import { Badges } from '~/components/Badges';
import { Loader } from '~/components/Loader';

import anonymousIcon from '@/icons/anonymous-transparent.svg';

export type SidebarUserProps = $Shape<Viewer> & {
  loading?: boolean,
  open?: boolean,
  palette?: string,
  passportNumber?: string,
  rank?: string,
  role?: string,
};

const size = ({ open }) => (open ? '80px' : '40px');

const SidebarUserWrapper = styled.div`
  position: relative;
  display: flex;
  padding: 14px 8px 0;
  height: 132px;
  align-items: center;
  flex-direction: column;
`;

const SidebarUserAvatar = styled(Avatar)`
  width: ${size};
  height: ${size};
  box-sizing: border-box;
`;

const SidebarUserTitle = styled.h2`
  color: ${palette('grayscale', 0, true)};
  font-family: ${font('bold')};
  font-size: 24px;
  text-transform: uppercase;
  text-align: center;

  ${ifProp(
    'isBadgesVisible',
    css`
      margin-top: 12px;
    `,
    css`
      margin-top: 10px;
    `,
  )}

  ${ifNotProp(
    'open',
    css`
      font-size: 8px;
    `,
  )};
`;

const SideBarUserLoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  & > svg {
    color: ${palette('grayscale', 0, true)};
    opacity: 0.8;
  }
`;

const SideBarUserBadgesWrapper = styled.div`
  position: absolute;
  left: ${props => (props.open ? 0 : 4)}px;
  top: ${props => (props.open ? 70 : 40)}px;

  & > div {
    margin-left: -9px;
  }
`;

/**
 * SidebarUser React-component
 * @memberof Sidebar
 * @extends Avatar
 * @param {boolean} [props.loading] - loading state
 * @param {string} [props.passportNumber] - passport number
 * @param {boolean} [props.open] - sidebar open state
 * @param {string} [props.rank] - user rank
 * @param {string} [props.role] - user role
 * @example <caption>Simple usage</caption>
 * <SidebarUser user={{ id: "Adam Wong" }} />
 * @returns {React.Node<*>} React-component
 */
export const SidebarUser = ({
  citizenType,
  hasDeposit,
  loading,
  open,
  passportNumber,
  rank,
  role,
}: SidebarUserProps) => {
  const badgeSize = open ? 31 : 24;
  const isBadgesVisible = !!(rank && role);

  return (
    <SidebarUserWrapper testid="sidebaruser">
      {loading ? (
        <SideBarUserLoaderWrapper>
          <Loader />
        </SideBarUserLoaderWrapper>
      ) : (
        <Fragment>
          <SidebarUserAvatar open={open} src={anonymousIcon}>
            {passportNumber}
          </SidebarUserAvatar>
          <SideBarUserBadgesWrapper open={open}>
            <Badges
              hasDeposit={hasDeposit}
              rank={rank}
              role={role}
              size={badgeSize}
              type={citizenType}
            />
          </SideBarUserBadgesWrapper>
          <SidebarUserTitle isBadgesVisible={isBadgesVisible} open={open}>
            {passportNumber}
          </SidebarUserTitle>
        </Fragment>
      )}
    </SidebarUserWrapper>
  );
};

SidebarUser.defaultProps = {
  palette: 'primary',
};
